import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import ReactMarkdown from 'react-markdown';

const OppoScroll = () => {
  const targetRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: targetRef,
  });

  return (
    <>
      <section ref={targetRef} className="flex bg-cocoa text-cream">
        <Content content={items} />
        <Images content={items} scrollYProgress={scrollYProgress} />
      </section>
    </>
  );
};

const Content = ({ content }) => {
  return (
    <div className="w-full">
      {content.map(({ id, title, description }, idx) => (
        <div
          key={id}
          className={`p-8 h-screen flex flex-col ${idx % 2 ? "bg-cocoa text-cream" : "bg-cream text-cocoa"
            }`}
        >
          <div className="flex flex-col h-full">
            <h3 className="text-4xl md:text-6xl font-medium mb-8">
              {title}
            </h3>
            <div className={`w-full h-1 mb-8 ${idx % 2 ? "bg-cream" : "bg-cocoa"}`} />
            <div className="flex-1 overflow-y-auto pr-4">
              <ReactMarkdown
                className="prose prose-lg max-w-none"
                components={{
                  p: ({ children }) => (
                    <p className="text-inherit mb-4">{children}</p>
                  ),
                  h1: ({ children }) => (
                    <h1 className="text-2xl font-bold mb-6">{children}</h1>
                  ),
                  h2: ({ children }) => (
                    <h2 className="text-xl font-semibold mb-4">{children}</h2>
                  ),
                  strong: ({ children }) => (
                    <strong className="font-semibold">{children}</strong>
                  ),
                  em: ({ children }) => <em className="italic">{children}</em>,
                }}
              >
                {description}
              </ReactMarkdown>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

const Images = ({ content, scrollYProgress }) => {
  const top = useTransform(
    scrollYProgress,
    [0, 1],
    [`-${(content.length - 1) * 100}vh`, "0vh"]
  );

  return (
    <div className="h-screen overflow-hidden sticky top-0 w-24 md:w-full">
      <motion.div style={{ top }} className="absolute left-0 right-0">
        {[...content].reverse().map(({ img, id, title }) => (
          <img
            key={id}
            alt={title}
            className="h-screen w-full object-cover"
            src={img}
          />
        ))}
      </motion.div>
    </div>
  );
};

export default OppoScroll;

const items = [
  {
    id: 1,
    title: "THEA – LA CUISINE by ARCLINEA",
    description: `# Innovation et esthétisme de la cuisine moderne

Avec **Thea**, **Arclinea** adopte une approche qui allie **minimalisme raffiné** et **fonctionnalité plus que design**, caractéristique des cuisines sans poignées aux lignes épurées. Le résultat est un espace **rationnel** et **sophistiqué**, offrant une **polyvalence** sans compromis.

**Thea** incarne les principes fondamentaux des conceptions d’**Arclinea**, tout en apportant une flexibilité **innovante** qui permet une **adaptabilité** optimale, notamment grâce à ses poignées modulables, pouvant être intégrées **horizontalement** ou **verticalement**.

Si son nom rend hommage à une création des années 60, **Thea** est aujourd’hui le cœur même de notre **évolution créative**, une véritable référence dans l’univers du mobilier haut de gamme.

Une cuisine **Théa** de chez **Arclinea**, est exposée au sein de notre Showroom, visible et configurable autrement ou dans d’autres modèles chez **ISILIVING** à Aix-en-Provence.`,
    img: "/assets/images/oppo-scroll/ARCLINEA - THEA COLLECTION VISUEL PHOTOS DEFILANTES.jpeg",
  },
  {
    id: 2,
    title: "PLATNER – LA COLLECTION by KNOLL",
    description: `# L'icône du design moderne et raffiné 

**Imaginée** en 1966 par **Warren Platner** pour **Knoll**, la collection **Platner** incarne l'élégance intemporelle du design. 

Née de la volonté de son créateur d'apporter une touche de raffinement et de délicatesse au modernisme, elle s'inspire des styles décoratifs classiques, tels que l'opulence du **Louis XV**, tout en conservant une esthétique contemporaine et sculpturale. 

Cette collection emblématique, à la fois sophistiquée et aérienne, marie avec brio **design architectural** et **artisanat d'exception**, faisant d'elle une référence incontournable dans l'univers du **mobilier haut de gamme**. 

Les produits de la collection **Platner** de chez **Knoll**, rendant plus élégant notre Showroom, visibles chez **ISILIVING** à Aix-en-Provence.`,
    img: "/assets/images/oppo-scroll/KNOLL - PLATNER COLLECTION VISUEL PHOTOS DEFILANTES.jpg",
  },
  {
    id: 3,
    title: "AMANU – LA NOUVELLE COLLECTION by TRIBU",
    description:
      `# L’élégance du mobilier d’extérieur avec un engagement durable\n\n 
La collection sophistiquée **Amanu** de chez **Tribù** repousse les frontières entre intérieur et extérieur, offrant un équilibre parfait entre **esthétique épurée**, **bien-être absolu** et **engagement durable**.

Son design raffiné allie harmonieusement **confort** et **élégance**, créant des espaces extérieurs à la fois chaleureux et intemporels.

Les chaises de repas se démarquent par leurs **assises suspendues en fibre de chanvre**, minutieusement tissées à la main, qui semblent flotter avec légèreté au sein d’un **cadre en teck massif** aux lignes épurées.

Au cœur de cette collection, le **canapé trois places** emblématique offre une assise généreuse et accueillante, parfaite pour partager des moments de détente.

Le **nouveau lit de jour** invite à la relaxation, que ce soit sous les rayons du soleil ou sous un ciel étoilé.

Une sélection de **tables basses aux formes variées** vient compléter l’ensemble, chacune reprenant l’élégance distinctive de la **table de repas Amanu**, apportant ainsi une **harmonie sophistiquée** aux espaces extérieurs.

Avec son design unique et ses matériaux d’exception, la **collection Amanu** incarne l’art de vivre en plein air dans toute sa splendeur.`,
    img: "/assets/images/oppo-scroll/TRIBU - AMANU COLLECTION VISUEL PHOTOS DEFILANTES.jpg",
  },
  {
    id: 4,
    title: "USM HALLER VERT OLIVE – LA COLLECTION by USM",
    description:
      `# Le style naturel et élégant intégré à la collection permanente\n\n
Lancée en **2023**, notre **collection spéciale Vert Olive** a rencontré un succès remarquable.

Nous sommes ravis d’annoncer l’intégration de cette gamme dans la collection permanente, venant compléter harmonieusement nos best-sellers.

Cette édition exclusive insuffle une **touche organique** et **raffinée** à tous les intérieurs, embellissant chaque espace avec son design intemporel et sa teinte naturelle sophistiquée.

Plusieurs meubles **USM** configurés de différentes manières subliment notre Showroom, tous visibles et configurable chez **ISILIVING** à Aix-en-Provence, avec le reste de la collection **USM Haller**.`,
    img: "/assets/images/oppo-scroll/USM - VERT OLIVE COLLECTION VISUEL PHOTOS DEFILANTES.jpg",
  },
]; 